/**
 * Client-side validation for the send-rate and per-action cap.
 *
 * ── What this is for, and what it is not ─────────────────────────────────────
 *
 * The server is authoritative. `parseRateLimitConfig` in backend/utils/emailRateLimiter.js
 * and `parseLimitToSend` in backend/routes/sendemails.js re-check every value and reject with
 * a 400. This exists only so a typo is reported instantly rather than costing a round
 * trip, and so the operator is not sent to the back of the campaign queue to be told
 * about a mistyped number.
 *
 * The bounds are passed in from /api/app-config rather than hardcoded. The old
 * frontend kept its own copies with a comment admitting they were duplicated from the
 * server and would drift — this removes the duplicate rather than relocating it.
 *
 * Messages are reproduced verbatim from the previous implementation. Operators have
 * read these strings for a long time and there is nothing to gain from rewording them.
 */

/** Normalises a form value to a trimmed string. */
function asText(value) {
  return value === null || value === undefined ? '' : String(value).trim();
}

/**
 * Validates the Limit / Interval pair.
 *
 * Both empty is valid and means "no rate limit", which is how every campaign behaved
 * before the interval field existed. An interval without a limit is the one
 * combination that is an error: the limit is what the interval is a window *for*, so
 * an interval alone does not describe a rate.
 *
 * @returns {string} An error message, or '' when valid.
 */
export function validateSendRate(limitRaw, intervalRaw, bounds) {
  const limitBounds = bounds?.rateLimit ?? { min: 1, max: 1_000_000 };
  const intervalBounds = bounds?.rateIntervalSeconds ?? { min: 0.1, max: 3600 };

  const limit = asText(limitRaw);
  const interval = asText(intervalRaw);

  if (limit !== '') {
    // Number(), not parseInt(): parseInt('35abc') is 35, which would accept a typo as
    // a valid rate and silently send at a rate nobody asked for.
    const value = Number(limit);

    if (!Number.isInteger(value)) {
      return `Limit must be a whole number of emails (got "${limit}").`;
    }
    if (value < limitBounds.min || value > limitBounds.max) {
      return `Limit must be between ${limitBounds.min} and ${limitBounds.max}.`;
    }
  }

  if (interval === '') return '';

  const seconds = Number(interval);

  if (!Number.isFinite(seconds)) {
    return `Interval (seconds) must be a number (got "${interval}").`;
  }
  if (seconds < intervalBounds.min || seconds > intervalBounds.max) {
    return `Interval (seconds) must be between ${intervalBounds.min} and ${intervalBounds.max}.`;
  }
  if (limit === '') {
    return 'Interval (seconds) needs a Limit — Limit is how many emails each interval allows.';
  }

  return '';
}

/**
 * Validates Limit to Send — the cap on one Send Email action.
 *
 * Deliberately independent of the rate check: this is a batch size, not a second
 * rate, so it neither requires nor constrains Limit and Interval. Empty means no cap.
 *
 * @returns {string} An error message, or '' when valid.
 */
export function validateLimitToSend(raw, bounds) {
  const limits = bounds?.limitToSend ?? { min: 1, max: 10_000_000 };
  const text = asText(raw);

  if (text === '') return '';

  const value = Number(text);

  if (!Number.isInteger(value)) {
    return `Limit to Send must be a whole number of emails (got "${text}").`;
  }
  if (value < limits.min) {
    return `Limit to Send must be at least ${limits.min} — leave it empty for no limit (got ${value}).`;
  }
  if (value > limits.max) {
    return `Limit to Send must be ${limits.max} or less (got ${value}).`;
  }

  return '';
}

/** The message shown for any field left empty. Matches the client's required-field UX. */
export const REQUIRED_FIELD_MESSAGE = 'This field is required.';

/**
 * Every field is mandatory except Custom Headers.
 *
 * Three fields keep an "empty is meaningful" exception, each a deliberate product
 * decision rather than an oversight:
 *   - `smtpPass` is required only when no password is stored server-side; an empty box
 *     with a saved credential means "use the saved one" (see resolveSmtpPass on the
 *     server). `hasSavedPassword` carries that fact in.
 *   - `inboxPatternId` is never "empty": the control always resolves to a value, with
 *     the empty string meaning the valid "Default" selection, so it is always satisfied.
 *   - `testRecipients` / `fileIds` are mode-exclusive — only the one belonging to the
 *     active Bulk/Test mode is required, because the other is disabled and cannot be
 *     filled.
 * Everything else (SMTP host/port/user, From Email/Name, Subject, Message Type, Message,
 * Content Transfer Encoding, Custom Message-ID, and the Limit / Interval / Limit to Send
 * trio) is required.
 */
const REQUIRED_TEXT_FIELDS = [
  'smtpHost',
  'smtpPort',
  'smtpUser',
  'fromEmail',
  'fromName',
  'subject',
  'messageType',
  'contentTransferEncoding',
  'message',
  'customMessageId'
];

/**
 * Validation that must pass before a submission is worth queueing.
 *
 * Returns the full set of field errors so the UI can highlight every invalid field at
 * once, plus the first one (in a stable, most-structural-first order) for the toast and
 * for callers that only need a single message.
 *
 * @returns {{ok: boolean, errors: Record<string,string>, error: string, field: string|null}}
 */
export function validateCampaignForm(form, bounds, options = {}) {
  const { hasSavedPassword = false } = options;
  const isTest = form.testBulk === 'Test';
  const errors = {};

  // Plain "must not be empty" fields, in the order the operator reads them.
  for (const field of REQUIRED_TEXT_FIELDS) {
    if (asText(form[field]) === '') errors[field] = REQUIRED_FIELD_MESSAGE;
  }

  // SMTP password: required only when nothing is stored server-side.
  if (!hasSavedPassword && asText(form.smtpPass) === '') {
    errors.smtpPass = REQUIRED_FIELD_MESSAGE;
  }

  // Return-Path: optional, but a non-empty value must be a usable envelope sender.
  if (asText(form.returnPath) !== '' && !isValidReturnPath(form.returnPath)) {
    errors.returnPath = RETURN_PATH_MESSAGE;
  }

  // Mode-exclusive recipients source.
  if (isTest) {
    if (!asText(form.testRecipients)) errors.testRecipients = REQUIRED_FIELD_MESSAGE;
  } else if (parseFileIds(form.fileIds).length === 0) {
    errors.fileIds = REQUIRED_FIELD_MESSAGE;
  }

  // Rate / cap trio: all three required, and still range-checked when present. The rate
  // check couples Limit and Interval, so its message is attributed to whichever of the
  // two it concerns.
  const rateError = validateSendRate(form.limit, form.intervalSeconds, bounds);
  if (asText(form.limit) === '') {
    errors.limit = REQUIRED_FIELD_MESSAGE;
  } else if (rateError && !/interval/i.test(rateError)) {
    errors.limit = rateError;
  }
  if (asText(form.intervalSeconds) === '') {
    errors.intervalSeconds = REQUIRED_FIELD_MESSAGE;
  } else if (rateError && /interval/i.test(rateError)) {
    errors.intervalSeconds = rateError;
  }
  const limitToSendError = validateLimitToSend(form.limitToSend, bounds);
  if (asText(form.limitToSend) === '') {
    errors.limitToSend = REQUIRED_FIELD_MESSAGE;
  } else if (limitToSendError) {
    errors.limitToSend = limitToSendError;
  }

  // First error, most-structural-first, for the toast and single-message callers.
  const ORDER = [
    'smtpHost', 'smtpPort', 'smtpUser', 'smtpPass',
    'fromEmail', 'returnPath', 'fromName', 'subject', 'messageType', 'contentTransferEncoding',
    'message', 'customMessageId',
    'testRecipients', 'fileIds',
    'limit', 'intervalSeconds', 'limitToSend'
  ];
  const firstField = ORDER.find((field) => errors[field]) || null;

  return {
    ok: firstField === null,
    errors,
    field: firstField,
    error: firstField ? errors[firstField] : ''
  };
}

/**
 * Splits the File IDs field.
 *
 * The first id is significant beyond being first: it becomes the campaign's
 * sessionId, so it is what /status is polled with, what /stop-sending halts and what
 * the campaign lane is keyed on.
 */
export function parseFileIds(raw) {
  return asText(raw)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

/** Splits Test Recipients on the same separators the server accepts. */
export function parseTestRecipients(raw) {
  return asText(raw)
    .split(/[,;\n\r]+/)
    .map((email) => email.trim())
    .filter(Boolean);
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email) {
  return EMAIL_PATTERN.test(String(email ?? '').trim());
}

// Mirrors backend/utils/returnPath.js. Stricter than EMAIL_PATTERN because the value
// goes verbatim into SMTP `MAIL FROM:<...>`: plain ASCII addr-spec, no <, >, quotes,
// commas or whitespace, at most 254 characters.
const RETURN_PATH_PATTERN =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export const RETURN_PATH_MESSAGE = 'Enter a valid email address, e.g. bounce@opterite.in.';

export function isValidReturnPath(value) {
  const text = String(value ?? '').trim();
  return text.length <= 254 && RETURN_PATH_PATTERN.test(text);
}
